import { useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import type { B2BCartItem } from '../types/b2b';

export interface PersistedCartState {
  items: B2BCartItem[];
  pickupDate: string;
  updatedAt: number;
  version: 1;
}

const STORAGE_VERSION = 1 as const;
const SERVER_DEBOUNCE_MS = 800;

const cartStorageKey = (buyerId: string) => `b2b-cart-state:${buyerId}`;

function readLocal(buyerId: string): PersistedCartState | null {
  try {
    const raw = localStorage.getItem(cartStorageKey(buyerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedCartState;
    if (parsed?.version !== STORAGE_VERSION || !Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocal(buyerId: string, state: PersistedCartState): void {
  try {
    localStorage.setItem(cartStorageKey(buyerId), JSON.stringify(state));
  } catch {
    // quota exceeded or storage disabled; silently ignore
  }
}

function clearLocal(buyerId: string): void {
  try {
    localStorage.removeItem(cartStorageKey(buyerId));
  } catch {
    // ignore
  }
}

const pendingRemoteWrites = new Map<string, ReturnType<typeof setTimeout>>();

function cancelPendingRemoteWrite(buyerId?: string): void {
  if (buyerId) {
    const timer = pendingRemoteWrites.get(buyerId);
    if (timer) {
      clearTimeout(timer);
      pendingRemoteWrites.delete(buyerId);
    }
    return;
  }
  for (const timer of pendingRemoteWrites.values()) {
    clearTimeout(timer);
  }
  pendingRemoteWrites.clear();
}

async function syncCartToServer(
  buyerId: string,
  adminId: string,
  state: PersistedCartState,
): Promise<void> {
  const { error } = await supabase
    .from('b2b_carts')
    .upsert(
      {
        buyer_id: buyerId,
        admin_id: adminId,
        items: state.items as unknown as object,
        pickup_date: state.pickupDate || null,
        updated_at: new Date(state.updatedAt).toISOString(),
      },
      { onConflict: 'buyer_id' },
    );
  if (error) {
    console.warn('Failed to sync B2B cart to server:', error.message);
  }
}

/** Flush debounced cart state before logout (audit F-LOSS-001). */
export async function flushB2BCartOnLogout(buyer: {
  id: string;
  admin_id: string;
}): Promise<void> {
  cancelPendingRemoteWrite(buyer.id);
  const state = readLocal(buyer.id);
  if (!state) return;
  try {
    await syncCartToServer(buyer.id, buyer.admin_id, state);
  } catch (err) {
    console.warn('Failed to flush B2B cart on logout:', err);
  }
}

/**
 * Hybrid persistence for the B2B cart.
 *
 * - localStorage gives instant read/write on the same device (zero-latency restore).
 * - A b2b_carts row in Supabase provides cross-device durability.
 * - Reconciliation uses last-write-wins on updatedAt (no item-level merge).
 *
 * Security note: this app uses custom JWT auth with RLS disabled. Access is
 * controlled by the client filtering on buyer_id, matching the existing
 * reservations / b2b_buyers pattern.
 */
export function useB2BCart() {
  const { buyer } = useAuth();

  const cancelPendingWrite = useCallback((buyerId?: string) => {
    cancelPendingRemoteWrite(buyerId);
  }, []);

  useEffect(() => () => cancelPendingRemoteWrite(), []);

  const loadPersisted = useCallback(async (): Promise<PersistedCartState | null> => {
    if (!buyer) return null;

    const local = readLocal(buyer.id);

    let remote: PersistedCartState | null = null;
    try {
      const { data, error } = await supabase
        .from('b2b_carts')
        .select('items, pickup_date, updated_at')
        .eq('buyer_id', buyer.id)
        .maybeSingle();

      if (!error && data) {
        const remoteUpdatedAt = data.updated_at ? new Date(data.updated_at).getTime() : 0;
        remote = {
          version: STORAGE_VERSION,
          items: Array.isArray(data.items) ? (data.items as B2BCartItem[]) : [],
          pickupDate: typeof data.pickup_date === 'string' ? data.pickup_date : '',
          updatedAt: Number.isFinite(remoteUpdatedAt) ? remoteUpdatedAt : 0,
        };
      }
    } catch (err) {
      console.warn('Failed to load remote B2B cart, falling back to local:', err);
    }

    if (local && remote) return remote.updatedAt > local.updatedAt ? remote : local;
    return remote ?? local;
  }, [buyer]);

  const persist = useCallback((items: B2BCartItem[], pickupDate: string): void => {
    if (!buyer) return;

    // Snapshot buyer identity at call time. JavaScript const-binding semantics
    // already guarantee the deferred timer sees this render's `buyer`, but
    // pinning to plain string consts makes the intent explicit and prevents
    // any future refactor (e.g., moving `buyer` to a ref or reassigning it)
    // from silently routing the delayed remote write to a different account.
    const buyerId = buyer.id;
    const adminId = buyer.admin_id;

    const state: PersistedCartState = {
      version: STORAGE_VERSION,
      items,
      pickupDate,
      updatedAt: Date.now(),
    };

    writeLocal(buyerId, state);

    cancelPendingWrite(buyerId);
    const timer = setTimeout(async () => {
      pendingRemoteWrites.delete(buyerId);
      try {
        await syncCartToServer(buyerId, adminId, state);
      } catch (err) {
        console.warn('Failed to sync B2B cart to server:', err);
      }
    }, SERVER_DEBOUNCE_MS);
    pendingRemoteWrites.set(buyerId, timer);
  }, [buyer, cancelPendingWrite]);

  const clearPersisted = useCallback(async (): Promise<void> => {
    if (!buyer) return;
    cancelPendingWrite();
    clearLocal(buyer.id);
    try {
      const { error } = await supabase
        .from('b2b_carts')
        .delete()
        .eq('buyer_id', buyer.id);
      if (error) console.warn('Failed to delete remote B2B cart:', error.message);
    } catch (err) {
      console.warn('Failed to delete remote B2B cart:', err);
    }
  }, [buyer, cancelPendingWrite]);

  return { loadPersisted, persist, clearPersisted };
}
